import Panel from '@/components/panel/Panel';
import { FiShuffle } from '@/components/react-icons/main';
import DividerV2 from '@/components/v2/DividerV2';
import selectors from '@/redux/selectors/selectors';
import statusIconMap from '@/schema/statusIconMap';
import getParentIterationStepIds from '@/screens/workflows/lib/getParentIterationStepIds';
import stepActions from '@/screens/workflows/redux/actions/stepActions';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import ArgumentLabel from '../ArgumentLabel';
import ArgumentWrapper from '../ArgumentWrapper';
import FlowControlSendOptions from '../FlowControlSendOptions';
import SliderClose from './SliderClose';
import SliderHeader from './SliderHeader';
import SliderTitle from './SliderTitle';

const onFailureOptions = [
  { value: 'stop', label: 'Stop workflow' },
  { value: 'next', label: 'Go to next step' },
  { value: 'continue', label: 'Continue loop' },
  { value: 'break', label: 'Break loop' }
];

const logOptions = [
  { value: null, label: 'None' },
  { value: 'error', label: 'Error', icon: statusIconMap('error') },
  { value: 'warn', label: 'Warning', icon: statusIconMap('warn') },
  { value: 'info', label: 'Info', icon: statusIconMap('info') }
];

const onNoResultsOptions = [
  { value: 'next', label: 'Go to next step' },
  { value: 'continue', label: 'Continue loop' },
  { value: 'break', label: 'Break loop' }
];

const FlowControlSlider = ({ onClose, disabled = false }) => {
  const apps = useSelector(selectors.appsMetadata);
  const steps = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.steps);
  const graph = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.graph);
  const stepId = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.id);
  const flowControl = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.options?.flowControl);
  const actionOperation = useSelector((state) => workflowScreenSelectors.activeWorkflowStepAppAction(state)?.operation);
  const action = useSelector((state) => workflowScreenSelectors.activeWorkflowStepAppAction(state));

  const iterationParentStepIds = getParentIterationStepIds(stepId, steps, apps, graph);
  const isIterateChild = iterationParentStepIds?.length > 0;
  const isReadOperation = actionOperation === 'read';
  const successFlowControl = flowControl?.onSuccess;
  const failureFlowControl = flowControl?.onFailure;
  const noResultsFlowControl = flowControl?.onNoResults;

  const handleChange = (key, value) => {
    stepActions.set(`options.flowControl.${key}`, value);
  };

  const handleChangeOnSuccess = (key, value) => {
    stepActions.set(`options.flowControl.onSuccess.${key}`, value);
  };

  const handleChangeOnFailure = (key, value) => {
    stepActions.set(`options.flowControl.onFailure.${key}`, value);
  };

  const handleChangeOnNoResults = (key, value) => {
    stepActions.set(`options.flowControl.onNoResults.${key}`, value);
  };

  const handleToggleFailure = (enabled) => {
    if (enabled) {
      const hasOperationSet = !!action?.operation;
      if (hasOperationSet && isIterateChild) {
        handleChange('onFailure', { action: 'continue', logLevel: 'warn' });
      } else {
        handleChange('onFailure', { action: 'stop', logLevel: 'error' });
      }
    } else {
      handleChange('onFailure', null);
    }
  };

  const handleToggleSuccess = (enabled) => {
    if (enabled) {
      handleChange('onSuccess', { logMessage: '' });
    } else {
      handleChange('onSuccess', null);
    }
  };

  const handleToggleNoResults = (enabled) => {
    if (enabled) {
      handleChange('onNoResults', { action: 'next' });
    } else {
      handleChange('onNoResults', null);
    }
  };

  const handleDeleteFlowControl = () => {
    stepActions.set(`options.flowControl`, {});
    onClose();
  };

  const handleDeleteSuccess = () => {
    handleChange('onSuccess', null);
  };

  const handleDeleteFailure = () => {
    handleChange('onFailure', null);
  };

  const handleDeleteNoResults = () => {
    handleChange('onNoResults', null);
  };

  const handleDeleteEmail = () => {
    handleChange('sendEmail', false);
  };

  useEffect(() => {
    if (!flowControl?.onFailure && !disabled) {
      handleToggleFailure(true);
    }
  }, [disabled]);

  return (
    <>
      <SliderHeader onClose={onClose} showDelete showRun onDelete={handleDeleteFlowControl} disabled={disabled} />
      <SliderTitle //
        icon={FiShuffle}
        title="Configure Flow Control"
        description="Set how the workflow should behave in certain conditions."
        helpIconContext="workflowStepFlowControlContext"
      />
      {/* On Failure */}
      <Panel.Section headerless gap="lg" onDelete={failureFlowControl ? handleDeleteFailure : null}>
        <Panel.Toggle //
          disabled={disabled}
          label="Enable On Failure?"
          className="flowControl-label"
          active={!!failureFlowControl}
          onChange={handleToggleFailure}
        />
        {!!failureFlowControl && (
          <>
            <ArgumentWrapper>
              <ArgumentLabel className={'small-gap'} label={'When this step fails:'} />
              <Panel.ToggleButton //
                disabled={disabled}
                className="padding-vertical no-wrap"
                options={isIterateChild ? onFailureOptions : onFailureOptions?.filter((o) => o.value !== 'continue' && o.value !== 'break')}
                value={failureFlowControl?.action}
                onChange={(value) => handleChange('onFailure.action', value)}
                noWrap
              />
            </ArgumentWrapper>
            <ArgumentWrapper>
              <ArgumentLabel className={'small-gap'} label={'Log Level'} />
              <Panel.ToggleButton //
                disabled={disabled}
                options={logOptions}
                value={failureFlowControl?.logLevel ?? null}
                onChange={(value) => handleChange('onFailure.logLevel', value)}
              />
            </ArgumentWrapper>
            {failureFlowControl?.logLevel && (
              <Panel.LexicalInput //
                disabled={disabled}
                label={'Log Message'}
                placeholder={'Failed to create record with value "X"'}
                value={failureFlowControl?.logMessage || ''}
                onChange={(value) => handleChange('onFailure.logMessage', value)}
                inputProps={{ style: { maxHeight: 150 } }}
                allowSelfOutput={true}
              />
            )}

            <FlowControlSendOptions disabled={disabled} level={'failure'} handleChange={handleChangeOnFailure} values={failureFlowControl} />
          </>
        )}
      </Panel.Section>
      {/* On Success */}
      <DividerV2 className="slider" />
      <Panel.Section headerless gap="lg" onDelete={successFlowControl ? handleDeleteSuccess : null}>
        <Panel.Toggle //
          disabled={disabled}
          label="Enable On Success?"
          className="flowControl-label"
          active={!!successFlowControl}
          onChange={handleToggleSuccess}
        />
        {!!successFlowControl && (
          <>
            <Panel.LexicalInput //
              disabled={disabled}
              label="Log Message"
              placeholder={'Created customer with value "X"'}
              value={successFlowControl?.logMessage || ''}
              onChange={(value) => handleChange('onSuccess.logMessage', value)}
              inputProps={{ style: { maxHeight: 150 } }}
              allowSelfOutput={true}
            />

            <FlowControlSendOptions disabled={disabled} level={'success'} handleChange={handleChangeOnSuccess} values={successFlowControl} />
          </>
        )}
      </Panel.Section>
      {/* On No Results */}
      {isReadOperation && (
        <>
          <DividerV2 className="slider" />
          <Panel.Section headerless gap="lg" onDelete={noResultsFlowControl ? handleDeleteNoResults : null}>
            <Panel.Toggle //
              disabled={disabled}
              label="Enable On No Results?"
              className="flowControl-label"
              active={!!noResultsFlowControl}
              onChange={handleToggleNoResults}
            />
            {!!noResultsFlowControl && (
              <>
                <ArgumentWrapper>
                  <ArgumentLabel className={'small-gap'} label={'Action'} />
                  <Panel.ToggleButton //
                    disabled={disabled}
                    className="padding-vertical"
                    options={isIterateChild ? onNoResultsOptions : onNoResultsOptions?.filter((o) => o.value !== 'continue')}
                    value={noResultsFlowControl?.action}
                    onChange={(value) => handleChange('onNoResults.action', value)}
                    noWrap
                  />
                </ArgumentWrapper>
                <ArgumentWrapper>
                  <ArgumentLabel className={'small-gap'} label={'Log Level'} />
                  <Panel.ToggleButton //
                    disabled={disabled}
                    options={logOptions}
                    value={noResultsFlowControl?.logLevel ?? null}
                    onChange={(value) => handleChange('onNoResults.logLevel', value)}
                  />
                </ArgumentWrapper>
                {noResultsFlowControl?.logLevel && (
                  <Panel.LexicalInput //
                    disabled={disabled}
                    label="Log Message"
                    placeholder={'Got no results from the step.'}
                    value={noResultsFlowControl?.logMessage || ''}
                    onChange={(value) => handleChange('onNoResults.logMessage', value)}
                    inputProps={{ style: { maxHeight: 150 } }}
                    allowSelfOutput={true}
                  />
                )}

                <FlowControlSendOptions disabled={disabled} level={'noResults'} handleChange={handleChangeOnNoResults} values={noResultsFlowControl} />
              </>
            )}
          </Panel.Section>
        </>
      )}

      {/* Keep it for backwards compatibility */}
      {flowControl?.sendEmail === true && (
        <Panel.Section title="Logging" collapsable={false} onDelete={handleDeleteEmail}>
          <FlowControlSendOptions disabled={disabled} handleChange={handleChange} values={flowControl} />
        </Panel.Section>
      )}

      <SliderClose onClose={onClose} />
    </>
  );
};

export default FlowControlSlider;
